import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Textarea } from '@/components/ui/Textarea';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Play, 
  Pause, 
  BarChart3, 
    Target,
  TrendingUp,
  Clock
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

interface ABTest {
  id: string;
  name: string;
  description: string;
  templateId: string;
  templateName: string;
  variantA: TestVariant;
  variantB: TestVariant;
  status: 'draft' | 'running' | 'completed' | 'paused';
  startDate?: string;
  endDate?: string;
  sampleSize: number;
  confidenceLevel: number;
  winner?: 'A' | 'B' | null;
  createdAt: string;
  updatedAt: string;
}

interface TestVariant {
  id: string;
  name: string;
  subject: string;
  content: string;
  sentCount: number;
  openedCount: number;
  clickedCount: number;
  repliedCount: number;
  openRate: number;
  clickRate: number;
  replyRate: number;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  category: string;
}

export const EmailABTestingPage: React.FC = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState<ABTest[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState<ABTest | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateId: '',
    variantA: {
      name: 'Versão A',
      subject: '',
      content: '',
    },
    variantB: {
      name: 'Versão B',
      subject: '',
      content: '',
    },
    sampleSize: 1000,
    confidenceLevel: 95,
  });

  const [activeTab, setActiveTab] = useState('tests');

  // Stats modal state
  interface VariantStats { sentCount: number; openedCount: number; clickedCount: number; openRate: number; clickRate: number; }
  interface TestStatsData {
    test: any;
    results: {
      variantA: VariantStats;
      variantB: VariantStats;
      statisticalSignificance: boolean;
      confidenceInterval: number;
    };
    totalEmails: number;
    emailsSent: number;
    emailsOpened: number;
    emailsClicked: number;
  }
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState<TestStatsData | null>(null);

  // Results/Insights state
  interface TestResultsData {
    variantA: VariantStats;
    variantB: VariantStats;
    statisticalSignificance: boolean;
    confidenceInterval: number;
    winner?: 'A' | 'B' | null;
  }
  const [selectedResultTestId, setSelectedResultTestId] = useState<string>('');
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsData, setResultsData] = useState<TestResultsData | null>(null);

  const confidenceLevels = [
    { value: 90, label: '90% (Menos rigoroso)' },
    { value: 95, label: '95% (Padrão)' },
    { value: 99, label: '99% (Mais rigoroso)' },
  ];

  const loadTests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/email-ab-testing');
      setTests(response.data.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar testes A/B');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await api.get('/email-templates');
      setTemplates(response.data.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar templates');
    }
  };

  const loadConfidenceLevels = async () => {
    try {
      await api.get('/email-ab-testing/confidence-levels');
      // Os confidence levels já estão definidos estaticamente, mas poderiam vir da API
    } catch (error: any) {
      console.warn('Erro ao carregar confidence levels:', error);
    }
  };

  useEffect(() => {
    loadTests();
    loadTemplates();
    loadConfidenceLevels();
  }, []);

  const handleCreateTest = async () => {
    try {
      await api.post('/email-ab-testing', formData);
      toast.success('Teste A/B criado com sucesso');
      setShowCreateModal(false);
      resetForm();
      loadTests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao criar teste A/B');
    }
  };

  const handleUpdateTest = async () => {
    if (!selectedTest) return;
    
    try {
      await api.put(`/email-ab-testing/${selectedTest.id}`, formData);
      toast.success('Teste A/B atualizado com sucesso');
      setShowEditModal(false);
      resetForm();
      loadTests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar teste A/B');
    }
  };

  const handleDeleteTest = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este teste A/B?')) return;
    
    try {
      await api.delete(`/email-ab-testing/${id}`);
      toast.success('Teste A/B excluído com sucesso');
      loadTests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao excluir teste A/B');
    }
  };

  const handleStartTest = async (id: string) => {
    try {
      await api.post(`/email-ab-testing/${id}/start`);
      toast.success('Teste A/B iniciado com sucesso');
      loadTests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao iniciar teste A/B');
    }
  };

  const handlePauseTest = async (id: string) => {
    try {
      await api.post(`/email-ab-testing/${id}/pause`);
      toast.success('Teste A/B pausado com sucesso');
      loadTests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao pausar teste A/B');
    }
  };

  // const handleCompleteTest = async (id: string) => {
  //   try {
  //     const response = await api.post(`/email-ab-testing/${id}/complete`);
  //     const result = response.data.data;
  //     toast.success('Teste A/B finalizado com sucesso');
  //     if (result.results?.winner) {
  //       toast.success(`Vencedor: Variante ${result.results.winner}`);
  //     }
  //     loadTests();
  //   } catch (error: any) {
  //     toast.error(error.response?.data?.message || 'Erro ao finalizar teste A/B');
  //   }
  // };

  // const handleSendTestEmail = async (id: string, variant: 'A' | 'B', recipients: string[]) => {
  //   try {
  //     const response = await api.post(`/email-ab-testing/${id}/send-test`, {
  //       variant,
  //       recipients
  //     });
  //     const result = response.data.data;
  //     toast.success(`${result.emailsSent} emails de teste enviados com sucesso`);
  //     if (result.errors && result.errors.length > 0) {
  //       console.warn('Erros no envio:', result.errors);
  //     }
  //   } catch (error: any) {
  //     toast.error(error.response?.data?.message || 'Erro ao enviar emails de teste');
  //   }
  // };

  const handleEndTest = async (id: string) => {
    try {
      await api.post(`/email-ab-testing/${id}/complete`);
      toast.success('Teste A/B finalizado com sucesso');
      loadTests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao finalizar teste A/B');
    }
  };

  const loadStats = async (id: string) => {
    try {
      setStatsLoading(true);
      const response = await api.get(`/email-ab-testing/${id}/stats`);
      setStatsData(response.data?.data || null);
      setShowStatsModal(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar estatísticas do teste A/B');
    } finally {
      setStatsLoading(false);
    }
  };

  const loadResults = async (id: string) => {
    if (!id) {
      setResultsData(null);
      return;
    }
    try {
      setResultsLoading(true);
      const response = await api.get(`/email-ab-testing/${id}/results`);
      setResultsData(response.data?.data || null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar resultados do teste A/B');
    } finally {
      setResultsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      templateId: '',
      variantA: {
        name: 'Versão A',
        subject: '',
        content: '',
      },
      variantB: {
        name: 'Versão B',
        subject: '',
        content: '',
      },
      sampleSize: 1000,
      confidenceLevel: 95,
    });
  };

  const openEditModal = (test: ABTest) => {
    setSelectedTest(test);
    setFormData({
      name: test.name,
      description: test.description,
      templateId: test.templateId,
      variantA: test.variantA,
      variantB: test.variantB,
      sampleSize: test.sampleSize,
      confidenceLevel: test.confidenceLevel,
    });
    setShowEditModal(true);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      draft: 'outline',
      running: 'default',
      completed: 'secondary',
      paused: 'destructive',
    };
    
    return (
      <Badge variant={(variants[status as keyof typeof variants] as any) || 'secondary'}>
        {status === 'draft' && 'Rascunho'}
        {status === 'running' && 'Executando'}
        {status === 'completed' && 'Concluído'}
        {status === 'paused' && 'Pausado'}
      </Badge>
    );
  };

  const getWinnerBadge = (winner: string | null) => {
    if (!winner) return null;
    
    return (
      <Badge variant="default" className="ml-2">
        Vencedor: {winner}
      </Badge>
    );
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  // Função para calcular significância estatística (não utilizada no momento)
  // const calculateStatisticalSignificance = (variantA: TestVariant, variantB: TestVariant) => {
  //   // Implementação simplificada de teste de significância estatística
  //   const difference = Math.abs(variantA.openRate - variantB.openRate);
  //   const pooledRate = (variantA.openedCount + variantB.openedCount) / (variantA.sentCount + variantB.sentCount);
  //   const standardError = Math.sqrt(pooledRate * (1 - pooledRate) * (1/variantA.sentCount + 1/variantB.sentCount));
  //   const zScore = difference / standardError;
  //   
  //   return zScore > 1.96; // 95% confidence level
  // };

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">A/B Testing de Emails</h1>
          <p className="text-muted-foreground">Teste diferentes versões de templates para otimizar performance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/configuracoes')}>← Voltar para Configurações &gt; E-mails</Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Teste A/B
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="tests" className="flex items-center">
            <Target className="w-4 h-4 mr-2" />
            Testes A/B
          </TabsTrigger>
          <TabsTrigger value="results" className="flex items-center">
            <BarChart3 className="w-4 h-4 mr-2" />
            Resultados
          </TabsTrigger>
          <TabsTrigger value="insights" className="flex items-center">
            <TrendingUp className="w-4 h-4 mr-2" />
            Insights
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tests" className="mt-6">
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : (
            <div className="grid gap-6">
              {tests.map((test) => (
                <Card key={test.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="flex items-center">
                          {test.name}
                          {getStatusBadge(test.status)}
                          {getWinnerBadge(test.winner || null)}
                        </CardTitle>
                        <p className="text-muted-foreground mt-1">{test.description}</p>
                      </div>
                      <div className="flex space-x-2">
                        {test.status === 'draft' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStartTest(test.id)}
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                        )}
                        {test.status === 'running' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePauseTest(test.id)}
                            >
                              <Pause className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEndTest(test.id)}
                            >
                              <Clock className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadStats(test.id)}
                          title="Ver estatísticas"
                        >
                          <BarChart3 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(test)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteTest(test.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium mb-2">Versão A</h4>
                        <div className="space-y-2 text-sm">
                          <p><strong>Assunto:</strong> {test.variantA.subject}</p>
                          <p className="text-xs text-muted-foreground">Abra estatísticas para ver métricas detalhadas</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2">Versão B</h4>
                        <div className="space-y-2 text-sm">
                          <p><strong>Assunto:</strong> {test.variantB.subject}</p>
                          <p className="text-xs text-muted-foreground">Abra estatísticas para ver métricas detalhadas</p>
                        </div>
                      </div>
                    </div>
                    
                    {test.status === 'running' && (
                      <div className="mt-4 p-3 bg-blue-50 rounded">
                        <p className="text-sm text-blue-800">
                          <strong>Teste em andamento:</strong> {test.variantA.sentCount + test.variantB.sentCount} de {test.sampleSize} emails enviados
                        </p>
                      </div>
                    )}
                    
                    {test.status === 'completed' && (
                      <div className="mt-4 p-3 bg-green-50 rounded">
                        <p className="text-sm text-green-800">
                          <strong>Teste concluído:</strong> 
                          {test.winner ? ` Versão ${test.winner} venceu` : ' Sem vencedor estatisticamente significativo'}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="results" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Resultados Detalhados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Selecionar Teste</label>
                  <Select
                    value={selectedResultTestId}
                    onValueChange={(value) => { setSelectedResultTestId(value); loadResults(value); }}
                  >
                    <option value="">Escolha um teste</option>
                    {tests.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </Select>
                </div>
              </div>

              {resultsLoading && (
                <div className="text-center py-8">Carregando resultados...</div>
              )}

              {!resultsLoading && resultsData && (
                <div className="space-y-6 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Abertura A</CardTitle></CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{(resultsData.variantA.openRate * 100).toFixed(1)}%</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Abertura B</CardTitle></CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{(resultsData.variantB.openRate * 100).toFixed(1)}%</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Clique A</CardTitle></CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{(resultsData.variantA.clickRate * 100).toFixed(1)}%</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Clique B</CardTitle></CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{(resultsData.variantB.clickRate * 100).toFixed(1)}%</div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>Comparativo</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={[
                          { variant: 'A', openRate: resultsData.variantA.openRate * 100, clickRate: resultsData.variantA.clickRate * 100 },
                          { variant: 'B', openRate: resultsData.variantB.openRate * 100, clickRate: resultsData.variantB.clickRate * 100 },
                        ]}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="variant" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="openRate" fill="#8884d8" name="Taxa de Abertura (%)" />
                          <Bar dataKey="clickRate" fill="#00C49F" name="Taxa de Clique (%)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Significância Estatística</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm">
                        {resultsData.statisticalSignificance ? (
                          <span>
                            Diferença significativa a {resultsData.confidenceInterval}% de confiança.
                          </span>
                        ) : (
                          <span>
                            Ainda não há significância estatística (nível {resultsData.confidenceInterval}%). Coleta adicional recomendada.
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Insights e Recomendações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Selecionar Teste</label>
                  <Select
                    value={selectedResultTestId}
                    onValueChange={(value) => { setSelectedResultTestId(value); loadResults(value); }}
                  >
                    <option value="">Escolha um teste</option>
                    {tests.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </Select>
                </div>
              </div>

              {resultsLoading && (
                <div className="text-center py-8">Carregando insights...</div>
              )}

              {!resultsLoading && resultsData && (
                <div className="space-y-4 mt-4">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Vencedor Sugerido</CardTitle></CardHeader>
                    <CardContent>
                      <div className="text-sm">
                        {(() => {
                          const aOpen = resultsData.variantA.openRate;
                          const bOpen = resultsData.variantB.openRate;
                          const aClick = resultsData.variantA.clickRate;
                          const bClick = resultsData.variantB.clickRate;
                          const winnerByOpen = aOpen === bOpen ? null : (aOpen > bOpen ? 'A' : 'B');
                          const winnerByClick = aClick === bClick ? null : (aClick > bClick ? 'A' : 'B');
                          if (resultsData.statisticalSignificance && winnerByOpen) {
                            return <span>Adote a versão {winnerByOpen} (abertura superior). Clique: {winnerByClick || 'empate'}.</span>;
                          }
                          return <span>Coletar mais dados antes de adotar um vencedor. Diferenças atuais não são significativas.</span>;
                        })()}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Deltas</CardTitle></CardHeader>
                    <CardContent>
                      <div className="text-sm space-y-1">
                        <div>Diferença de abertura: {((resultsData.variantA.openRate - resultsData.variantB.openRate) * 100).toFixed(1)} pp</div>
                        <div>Diferença de clique: {((resultsData.variantA.clickRate - resultsData.variantB.clickRate) * 100).toFixed(1)} pp</div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Próximas Ações</CardTitle></CardHeader>
                    <CardContent>
                      <ul className="list-disc ml-5 text-sm space-y-1">
                        <li>Se significativo, finalize o teste e adote o assunto/template da versão vencedora.</li>
                        <li>Se não significativo, aumente o tamanho da amostra ou prolongue a duração.</li>
                        <li>Teste também variações de pré-cabeçalho e CTA para melhorar cliques.</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Test Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Novo Teste A/B"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Nome do Teste</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Teste de assunto - Lembrete vencimento"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Descrição</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descreva o objetivo do teste"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Template Base</label>
            <Select
              value={formData.templateId}
              onValueChange={(value) => setFormData({ ...formData, templateId: value })}
            >
              <option value="">Selecione um template</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.category})
                </option>
              ))}
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Tamanho da Amostra</label>
              <Input
                type="number"
                value={formData.sampleSize}
                onChange={(e) => setFormData({ ...formData, sampleSize: parseInt(e.target.value) || 1000 })}
                placeholder="1000"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Nível de Confiança</label>
              <Select
                value={formData.confidenceLevel.toString()}
                onValueChange={(value) => setFormData({ ...formData, confidenceLevel: parseInt(value) })}
              >
                {confidenceLevels.map((level) => (
                  <option key={level.value} value={level.value.toString()}>
                    {level.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <Tabs value="variantA" onValueChange={() => {}} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="variantA">Versão A</TabsTrigger>
              <TabsTrigger value="variantB">Versão B</TabsTrigger>
            </TabsList>

            <TabsContent value="variantA" className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nome da Versão</label>
                <Input
                  value={formData.variantA.name}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variantA: { ...formData.variantA, name: e.target.value }
                  })}
                  placeholder="Versão A"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Assunto</label>
                <Input
                  value={formData.variantA.subject}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variantA: { ...formData.variantA, subject: e.target.value }
                  })}
                  placeholder="Assunto da versão A"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Conteúdo</label>
                <Textarea
                  value={formData.variantA.content}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variantA: { ...formData.variantA, content: e.target.value }
                  })}
                  placeholder="Conteúdo da versão A"
                  rows={6}
                />
              </div>
            </TabsContent>

            <TabsContent value="variantB" className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nome da Versão</label>
                <Input
                  value={formData.variantB.name}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variantB: { ...formData.variantB, name: e.target.value }
                  })}
                  placeholder="Versão B"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Assunto</label>
                <Input
                  value={formData.variantB.subject}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variantB: { ...formData.variantB, subject: e.target.value }
                  })}
                  placeholder="Assunto da versão B"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Conteúdo</label>
                <Textarea
                  value={formData.variantB.content}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variantB: { ...formData.variantB, content: e.target.value }
                  })}
                  placeholder="Conteúdo da versão B"
                  rows={6}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={() => setShowCreateModal(false)}>
            Cancelar
          </Button>
          <Button onClick={handleCreateTest}>
            Criar Teste A/B
          </Button>
        </div>
      </Modal>

      {/* Edit Test Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Editar Teste A/B"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Nome do Teste</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Teste de assunto - Lembrete vencimento"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Descrição</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descreva o objetivo do teste"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Template Base</label>
            <Select
              value={formData.templateId}
              onValueChange={(value) => setFormData({ ...formData, templateId: value })}
            >
              <option value="">Selecione um template</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.category})
                </option>
              ))}
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Tamanho da Amostra</label>
              <Input
                type="number"
                value={formData.sampleSize}
                onChange={(e) => setFormData({ ...formData, sampleSize: parseInt(e.target.value) || 1000 })}
                placeholder="1000"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Nível de Confiança</label>
              <Select
                value={formData.confidenceLevel.toString()}
                onValueChange={(value) => setFormData({ ...formData, confidenceLevel: parseInt(value) })}
              >
                {confidenceLevels.map((level) => (
                  <option key={level.value} value={level.value.toString()}>
                    {level.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <Tabs value="variantA" onValueChange={() => {}} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="variantA">Versão A</TabsTrigger>
              <TabsTrigger value="variantB">Versão B</TabsTrigger>
            </TabsList>

            <TabsContent value="variantA" className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nome da Versão</label>
                <Input
                  value={formData.variantA.name}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variantA: { ...formData.variantA, name: e.target.value }
                  })}
                  placeholder="Versão A"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Assunto</label>
                <Input
                  value={formData.variantA.subject}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variantA: { ...formData.variantA, subject: e.target.value }
                  })}
                  placeholder="Assunto da versão A"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Conteúdo</label>
                <Textarea
                  value={formData.variantA.content}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variantA: { ...formData.variantA, content: e.target.value }
                  })}
                  placeholder="Conteúdo da versão A"
                  rows={6}
                />
              </div>
            </TabsContent>

            <TabsContent value="variantB" className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nome da Versão</label>
                <Input
                  value={formData.variantB.name}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variantB: { ...formData.variantB, name: e.target.value }
                  })}
                  placeholder="Versão B"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Assunto</label>
                <Input
                  value={formData.variantB.subject}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variantB: { ...formData.variantB, subject: e.target.value }
                  })}
                  placeholder="Assunto da versão B"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Conteúdo</label>
                <Textarea
                  value={formData.variantB.content}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    variantB: { ...formData.variantB, content: e.target.value }
                  })}
                  placeholder="Conteúdo da versão B"
                  rows={6}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={() => setShowEditModal(false)}>
            Cancelar
          </Button>
          <Button onClick={handleUpdateTest}>
            Atualizar Teste A/B
          </Button>
        </div>
      </Modal>

      {/* Stats Modal */}
      <Modal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        title="Estatísticas do Teste A/B"
      >
        <div className="space-y-3">
          {statsLoading && <div className="text-center py-6">Carregando...</div>}
          {!statsLoading && statsData && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded p-3">
                  <h4 className="font-medium mb-2">Versão A</h4>
                  <p className="text-sm">Enviados: {statsData.results.variantA.sentCount}</p>
                  <p className="text-sm">Abertos: {statsData.results.variantA.openedCount}</p>
                  <p className="text-sm">Cliques: {statsData.results.variantA.clickedCount}</p>
                  <p className="text-sm">Taxa de Abertura: {formatPercentage(statsData.results.variantA.openRate)}</p>
                  <p className="text-sm">Taxa de Clique: {formatPercentage(statsData.results.variantA.clickRate)}</p>
                </div>
                <div className="border rounded p-3">
                  <h4 className="font-medium mb-2">Versão B</h4>
                  <p className="text-sm">Enviados: {statsData.results.variantB.sentCount}</p>
                  <p className="text-sm">Abertos: {statsData.results.variantB.openedCount}</p>
                  <p className="text-sm">Cliques: {statsData.results.variantB.clickedCount}</p>
                  <p className="text-sm">Taxa de Abertura: {formatPercentage(statsData.results.variantB.openRate)}</p>
                  <p className="text-sm">Taxa de Clique: {formatPercentage(statsData.results.variantB.clickRate)}</p>
                </div>
              </div>
              <div className="border rounded p-3">
                <p className="text-sm">Total de emails: {statsData.totalEmails}</p>
                <p className="text-sm">Enviados: {statsData.emailsSent} • Abertos: {statsData.emailsOpened} • Cliques: {statsData.emailsClicked}</p>
                <p className="text-sm">Significância estatística: {statsData.results.statisticalSignificance ? 'Sim' : 'Não'} ({statsData.results.confidenceInterval}%)</p>
              </div>
            </>
          )}
          {!statsLoading && !statsData && (
            <div className="text-sm text-muted-foreground">Nenhum dado disponível</div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setShowStatsModal(false)}>Fechar</Button>
        </div>
      </Modal>
    </div>
  );
};
